import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, FileText, FileSpreadsheet, File } from "lucide-react";
import PayslipViewModal from "./PaySlipViewModal";

const PayslipTable = ({ data = [], onExport }) => {
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayslipEntry, setSelectedPayslipEntry] = useState(null);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const openViewModal = (entry) => {
    setSelectedPayslipEntry(entry);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedPayslipEntry(null);
  };
  
  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExport = (format) => {
    setExportDropdownOpen(false);
    if (onExport) {
      onExport(format);
    }
  };

  const capitalize = (str) => {
    if (!str) return '';
    return str.replace(/_/g, ' ').split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
  };

  if (data.length === 0) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-sm mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">Payslip History</h3>
          <div className="flex gap-2">
            <div className="relative inline-block text-left" ref={dropdownRef}>
              <button
                onClick={() => setExportDropdownOpen((open) => !open)}
                type="button"
                className="inline-flex justify-center items-center w-full rounded-md bg-orange-600 px-4 py-2 text-white text-sm font-medium hover:bg-orange-700"
                aria-haspopup="true"
                aria-expanded={exportDropdownOpen}
              >
                Export
                <ChevronDown className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
              </button>
              {exportDropdownOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="export-menu-button">
                    <button onClick={() => handleExport("excel")} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900" role="menuitem">
                      <FileSpreadsheet size={16} /> Excel
                    </button>
                    <button onClick={() => handleExport("csv")} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900" role="menuitem">
                      <FileText size={16} /> CSV
                    </button>
                    <button onClick={() => handleExport("pdf")} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900" role="menuitem">
                      <File size={16} /> PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <p className="text-center text-gray-500 py-4">No payslip history to display.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white p-4 rounded-lg shadow-sm mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">Payslip History</h3>
          <div className="relative inline-block text-left" ref={dropdownRef}>
            <button
              onClick={() => setExportDropdownOpen((open) => !open)}
              type="button"
              className="inline-flex justify-center items-center w-full rounded-md bg-orange-600 px-4 py-2 text-white text-sm font-medium hover:bg-orange-700"
              aria-haspopup="true"
              aria-expanded={exportDropdownOpen}
            >
              Export
              <ChevronDown className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
            </button>
            {exportDropdownOpen && (
              <div
                className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                role="menu"
                aria-orientation="vertical"
              >
                <div className="py-1">
                  <button onClick={() => handleExport("excel")} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900" role="menuitem">
                    <FileSpreadsheet size={16} /> Excel
                  </button>
                  <button onClick={() => handleExport("csv")} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900" role="menuitem">
                    <FileText size={16} /> CSV
                  </button>
                  <button onClick={() => handleExport("pdf")} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900" role="menuitem">
                    <File size={16} /> PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-orange-100">
              <tr>
                <th className="p-3 text-left text-orange-600 font-semibold">Name</th>
                <th className="p-3 text-left text-orange-600 font-semibold">Department</th>
                <th className="p-3 text-left text-orange-600 font-semibold">Month</th>
                <th className="p-3 text-left text-orange-600 font-semibold">Gross Pay</th>
                {/* <th className="p-3 text-left text-orange-600 font-semibold">Payment Status</th> */}
                <th className="p-3 text-left text-orange-600 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((entry) => {
                const staffName = entry.staff ? `${entry.staff.firstName} ${entry.staff.lastName}` : 'N/A';
                const department = entry.staff ? entry.staff.department : 'N/A';
                
                const statusColor = {
                  pending: 'bg-yellow-100 text-yellow-700',
                  processing: 'bg-blue-100 text-blue-700',
                  paid: 'bg-green-100 text-green-700',
                  cancelled: 'bg-red-100 text-red-700',
                };
                
                const status = entry.paymentStatus || 'N/A';
                const statusClass = statusColor[status] || 'bg-gray-100 text-gray-700';

                return (
                  <tr key={entry._id} className="hover:bg-gray-50">
                    <td className="p-3 whitespace-nowrap">{staffName}</td>
                    <td className="p-3 whitespace-nowrap">{department}</td>
                    <td className="p-3 whitespace-nowrap">{entry.month}</td>
                    <td className="p-3 whitespace-nowrap font-medium text-green-600">${entry.grossPay?.toFixed(2) || '0.00'}</td>
                    <td className="p-3 whitespace-nowrap">
                      <span className={`${statusClass} text-xs px-2 py-1 rounded-full`}>
                        {capitalize(status)}
                      </span>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <button
                        onClick={() => openViewModal(entry)}
                        className="inline-flex items-center gap-1 border border-orange-600 text-orange-600 rounded px-3 py-1 text-sm hover:bg-orange-600 hover:text-white transition"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {showViewModal && (
        <PayslipViewModal
          entry={selectedPayslipEntry}
          isOpen={showViewModal}
          onClose={closeViewModal}
        />
      )}
    </>
  );
};

export default PayslipTable;